import { useState, useCallback, useEffect, useRef } from 'react';
import { api, ArcheReviewSummary, ArcheReviewDetail } from '@/lib/api';

interface UseArcheReviewsOptions {
  archId: string;
  pollInterval?: number;
}

interface UseArcheReviewsReturn {
  reviews: ArcheReviewSummary[];
  isLoading: boolean;
  error: Error | null;
  requestReview: () => Promise<{ reviewId: string; estimatedCostCents: number } | null>;
  refresh: () => Promise<void>;
}

export function useArcheReviews({
  archId,
  pollInterval,
}: UseArcheReviewsOptions): UseArcheReviewsReturn {
  const [reviews, setReviews] = useState<ArcheReviewSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const fetchReviews = useCallback(async () => {
    if (!archId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.listArcheReviews(archId);
      setReviews(response.reviews);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch reviews');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [archId]);

  // Track whether any reviews are in a pending/active state for polling
  const hasPendingRef = useRef(false);
  hasPendingRef.current = reviews.some((r) => r.status === 'pending' || r.status === 'in_progress');

  // Fetch reviews on mount and when archId changes (via fetchReviews identity)
  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Set up polling separately — only while there are pending reviews
  useEffect(() => {
    if (!pollInterval || !hasPendingRef.current) return;

    pollRef.current = setInterval(fetchReviews, pollInterval);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [fetchReviews, pollInterval]);

  const requestReview = useCallback(async (): Promise<{
    reviewId: string;
    estimatedCostCents: number;
  } | null> => {
    if (!archId) return null;

    setError(null);

    try {
      const response = await api.requestArcheReview(archId);
      // Refresh reviews list
      await fetchReviews();
      return {
        reviewId: response.reviewId,
        estimatedCostCents: response.estimatedCostCents,
      };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to request review');
      setError(error);
      return null;
    }
  }, [archId, fetchReviews]);

  return {
    reviews,
    isLoading,
    error,
    requestReview,
    refresh: fetchReviews,
  };
}

interface UseArcheReviewDetailOptions {
  archId: string;
  reviewId: string;
  pollWhilePending?: boolean;
}

interface UseArcheReviewDetailReturn {
  review: ArcheReviewDetail | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useArcheReviewDetail({
  archId,
  reviewId,
  pollWhilePending = true,
}: UseArcheReviewDetailOptions): UseArcheReviewDetailReturn {
  const [review, setReview] = useState<ArcheReviewDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const fetchReview = useCallback(async () => {
    if (!archId || !reviewId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.getArcheReview(archId, reviewId);
      setReview(response.review);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch review');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [archId, reviewId]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  // Poll while pending
  useEffect(() => {
    if (
      pollWhilePending &&
      review &&
      (review.status === 'pending' || review.status === 'in_progress')
    ) {
      pollRef.current = setInterval(fetchReview, 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [pollWhilePending, review?.status, fetchReview, review]);

  return {
    review,
    isLoading,
    error,
    refresh: fetchReview,
  };
}
